import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from '@/shared/hooks/useLocation';
import {
  referralsApi,
  type DirectoryPracticeInput,
  type OutcomeInput,
  type SendInput,
  type ReferralDraftInput,
  type ReferralSettings,
  type ReportInput,
  type StatusChangeInput,
} from '../api/referralsApi';
import { referralKeys } from '../queryKeys';

export function useReferralLocationId(): string {
  const { currentLocation } = useLocation();
  return currentLocation?.id ?? '';
}

export function useReferralTemplate(recipientPracticeId: string | null) {
  const locationId = useReferralLocationId();
  return useQuery({
    queryKey: referralKeys.template(locationId, recipientPracticeId),
    queryFn: () => referralsApi.getTemplate(locationId, recipientPracticeId),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useReferralSettings() {
  const locationId = useReferralLocationId();
  return useQuery({
    queryKey: referralKeys.settings(locationId),
    queryFn: () => referralsApi.getSettings(locationId),
    enabled: !!locationId,
  });
}

export function useReferralStats() {
  const locationId = useReferralLocationId();
  return useQuery({
    queryKey: referralKeys.stats(locationId),
    queryFn: () => referralsApi.getStats(locationId),
    enabled: !!locationId,
    staleTime: 60_000,
  });
}

export function useReferralList(filter: { box: string; status: string; q: string }) {
  const locationId = useReferralLocationId();
  const query = useQuery({
    queryKey: referralKeys.list(locationId, filter.box, filter.status, filter.q),
    queryFn: () => referralsApi.list(locationId, filter),
    enabled: !!locationId,
    refetchInterval: 60_000,
  });
  return { ...query, locationId };
}

export function useReferral(id: string | undefined) {
  const locationId = useReferralLocationId();
  const query = useQuery({
    queryKey: referralKeys.detail(locationId, id ?? ''),
    queryFn: () => referralsApi.get(locationId, id!),
    enabled: !!locationId && !!id,
  });
  return { ...query, locationId };
}

export function useDirectorySearch(search: string) {
  const locationId = useReferralLocationId();
  return useQuery({
    queryKey: referralKeys.directorySearch(locationId, search),
    queryFn: () => referralsApi.searchDirectory(locationId, search),
    enabled: !!locationId && search.trim().length >= 2,
    staleTime: 60_000,
  });
}

export function useSuggestedRecipients(input: { specialty?: string; reasons: string[]; comment?: string }) {
  const locationId = useReferralLocationId();
  const commentKey = (input.comment ?? '').trim().slice(0, 200);
  return useQuery({
    queryKey: referralKeys.suggested(locationId, input.specialty ?? '', input.reasons.join('|'), commentKey),
    queryFn: () => referralsApi.suggested(locationId, { ...input, comment: commentKey || undefined }),
    enabled: !!locationId,
    staleTime: 5 * 60 * 1000,
  });
}

/** How a slip could reach a prospective office right now (for the selected-office card). */
export function useDeliveryOptions(referralPracticeId: string | null) {
  const locationId = useReferralLocationId();
  return useQuery({
    queryKey: referralKeys.directoryDelivery(locationId, referralPracticeId ?? ''),
    queryFn: () => referralsApi.getDeliveryOptions(locationId, referralPracticeId!),
    enabled: !!locationId && !!referralPracticeId,
    staleTime: 60_000,
  });
}

function useInvalidateReferrals() {
  const queryClient = useQueryClient();
  const locationId = useReferralLocationId();
  return (id?: string) => {
    queryClient.invalidateQueries({ queryKey: referralKeys.listPrefix(locationId) });
    queryClient.invalidateQueries({ queryKey: referralKeys.stats(locationId) });
    if (id) queryClient.invalidateQueries({ queryKey: referralKeys.detail(locationId, id) });
  };
}

export function useReferralMutations() {
  const locationId = useReferralLocationId();
  const queryClient = useQueryClient();
  const invalidate = useInvalidateReferrals();

  const create = useMutation({
    mutationFn: (input: Omit<ReferralDraftInput, 'locationId'>) => referralsApi.create({ ...input, locationId }),
    onSuccess: () => invalidate(),
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<ReferralDraftInput, 'locationId'> }) =>
      referralsApi.update(id, { ...input, locationId }),
    onSuccess: (detail) => {
      queryClient.setQueryData(referralKeys.detail(locationId, detail.id), detail);
      invalidate();
    },
  });
  const send = useMutation({
    mutationFn: ({ id, input }: { id: string; input?: SendInput }) => referralsApi.send(locationId, id, input),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const addRecipient = useMutation({
    mutationFn: ({
      id,
      input,
    }: {
      id: string;
      input: { referralPracticeId: string; preferredProviderId?: string | null; preferredProviderName?: string | null };
    }) => referralsApi.addRecipient(locationId, id, input),
    onSuccess: (detail) => {
      queryClient.setQueryData(referralKeys.detail(locationId, detail.id), detail);
      invalidate();
    },
  });
  const updateRecipient = useMutation({
    mutationFn: ({
      id,
      recipientId,
      input,
    }: {
      id: string;
      recipientId: string;
      input: { preferredProviderId?: string | null; preferredProviderName?: string | null };
    }) => referralsApi.updateRecipient(locationId, id, recipientId, input),
    onSuccess: (detail) => queryClient.setQueryData(referralKeys.detail(locationId, detail.id), detail),
  });
  const removeRecipient = useMutation({
    mutationFn: ({ id, recipientId }: { id: string; recipientId: string }) => referralsApi.removeRecipient(locationId, id, recipientId),
    onSuccess: (detail) => {
      queryClient.setQueryData(referralKeys.detail(locationId, detail.id), detail);
      invalidate();
    },
  });
  const recordOutcome = useMutation({
    mutationFn: ({ id, input }: { id: string; input: OutcomeInput }) => referralsApi.recordOutcome(locationId, id, input),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const cancel = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => referralsApi.cancel(locationId, id, reason),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const remove = useMutation({
    mutationFn: (id: string) => referralsApi.deleteDraft(locationId, id),
    onSuccess: () => invalidate(),
  });
  const remind = useMutation({
    mutationFn: ({ id, recipientId }: { id: string; recipientId?: string | null }) => referralsApi.remind(locationId, id, recipientId),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const retryPatientText = useMutation({
    mutationFn: (id: string) => referralsApi.retryPatientText(locationId, id),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const changeStatus = useMutation({
    mutationFn: ({ id, input }: { id: string; input: StatusChangeInput }) =>
      referralsApi.changeStatus(locationId, id, input),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const addNote = useMutation({
    mutationFn: ({ id, note }: { id: string; note: string }) => referralsApi.addNote(locationId, id, note),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const addReport = useMutation({
    mutationFn: ({ id, input }: { id: string; input: ReportInput }) => referralsApi.addReport(locationId, id, input),
    onSuccess: (detail) => invalidate(detail.id),
  });
  const deleteAttachment = useMutation({
    mutationFn: ({ id, attachmentId }: { id: string; attachmentId: string }) =>
      referralsApi.deleteAttachment(locationId, id, attachmentId),
    onSuccess: (_r, { id }) => invalidate(id),
  });
  const createDirectoryPractice = useMutation({
    mutationFn: (input: Omit<DirectoryPracticeInput, 'locationId'>) =>
      referralsApi.createDirectoryPractice({ ...input, locationId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['referrals', 'directory'] }),
  });
  const updateDirectoryPractice = useMutation({
    mutationFn: ({ id, input }: { id: string; input: Partial<Omit<DirectoryPracticeInput, 'locationId'>> }) =>
      referralsApi.updateDirectoryPractice(id, { ...input, locationId }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['referrals', 'directory'] }),
  });
  const addProvider = useMutation({
    mutationFn: ({ practiceId, name }: { practiceId: string; name: string }) =>
      referralsApi.addProvider(practiceId, { locationId, name }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['referrals', 'directory'] }),
  });
  const saveSettings = useMutation({
    mutationFn: (patch: Partial<ReferralSettings>) => referralsApi.updateSettings({ ...patch, locationId }),
    onSuccess: (settings) => {
      queryClient.setQueryData(referralKeys.settings(locationId), settings);
      queryClient.invalidateQueries({ queryKey: ['referrals', 'template', locationId] });
    },
  });

  return {
    locationId,
    create,
    update,
    send,
    addRecipient,
    updateRecipient,
    removeRecipient,
    recordOutcome,
    cancel,
    remove,
    remind,
    retryPatientText,
    changeStatus,
    addNote,
    addReport,
    deleteAttachment,
    createDirectoryPractice,
    updateDirectoryPractice,
    addProvider,
    saveSettings,
    invalidate,
  };
}
